import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import { Waypoint } from "react-waypoint";
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { optionDecorator } from '../CustomAutoComplete/AutoCompleteListItemDecorator';
import useDebounce from '../../../hooks/useDebounce';

const AsynAutoComplete = ({
  label,
  pageSize = 20,
  isSearchOnType,
  selectedItem,
  isOptionEqualToValue,
  getOptionLabel,
  onValueChange,
  onFetchData,
  onTransformData,
  onFilterOptions,
  multiple,
  mode,
  name,
  error,
  helperText,
  isDisabled,
  parameter,
  renderOptionDecorator,
  isVisibleSourceSystem = true,
  isHover = false,
  required
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [inputValue, setInputValue] = useState('');
  const debouncedInputValue = useDebounce(inputValue, 500);
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const listBoxRef = useRef(null);
  const activeRef = useRef(true);
  const isRestoringScrollRef = useRef(false); // ADD THIS

  let onInputChangeHandler;
  let filterOptions;

  if (isSearchOnType) {
    onInputChangeHandler = (event, newInputValue) => {
      setInputValue(newInputValue);
    };

    filterOptions = (x) => x;
  }
  const fetchDataCallback = useCallback(async () => {
    if (!isOpen) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const scrollTop = listBoxRef.current ? listBoxRef.current.scrollTop : 0;

    const { locationIds, sourceSystemIds, divisionIds } = parameter || {};
    let data = await onFetchData({
      text: debouncedInputValue,
      locationIds,
      sourceSystemIds,
      divisionIds,
      page,
      pageSize,
    });

    if (onTransformData) {
      data = onTransformData(data);
    }

    if (activeRef.current) {
      if (page === 1) {
        let filterOptionItems = data;
        if (onFilterOptions) {
          filterOptionItems = onFilterOptions(data);
        }
        setOptions(filterOptionItems);
      } else {
        setOptions((prepOptions) => {
          if (onFilterOptions) {
            return onFilterOptions(prepOptions.concat(data));
          }
          return prepOptions.concat(data);
        });

        // Restore scroll position only when appending (page > 1)
        setTimeout(() => {
          if (listBoxRef.current) {
            isRestoringScrollRef.current = true; // SET FLAG BEFORE SCROLL
            listBoxRef.current.scrollTo({ top: scrollTop });
            setTimeout(() => {
              isRestoringScrollRef.current = false; // RESET FLAG AFTER SCROLL
            }, 50);
          }
        }, 0);
      }

      if (mode === 'Remote') {
        setHasMore(data.length >= pageSize);
      }

      setIsLoading(false);
    }
  }, [debouncedInputValue, isOpen, mode, onFetchData, onFilterOptions, onTransformData, page, pageSize, parameter]);

  useEffect(() => {
    // let active = true;
    activeRef.current = true;

    fetchDataCallback();

    // eslint-disable-next-line consistent-return
    return () => {
      // active = false;
      activeRef.current = false;
    };
  }, [fetchDataCallback, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setOptions([]);
    } else {
      setPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isSearchOnType) {
      setPage(1);
    }
  }, [isSearchOnType]);

  const onChangeHandler = (event, newValue) => {
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  const loadMoreResults = () => {
    if (hasMore && !isLoading) { // ADD !isLoading CHECK
      setPage((prepVal) => prepVal + 1);
    }
  };

  const onOpenHandler = () => {
    setIsOpen(true);
  };

  const onCloseHandler = () => {
    setIsOpen(false);
  };

  const renderOptionHandler = useMemo(() => (props, option) => {
    const { id, text } = option;
    if (optionDecorator) {
      const decorator = optionDecorator(props, option, renderOptionDecorator, isVisibleSourceSystem, isHover);
      if (decorator) {
        return decorator;
      }
    }

    if (id) {
      return (
        <li {...props} key={id}>
          {text || ' '}
        </li>
      );
    }

    return (
      <li {...props} key={option}>
        {option || ' '}
      </li>
    );
  }, [isHover, isVisibleSourceSystem, renderOptionDecorator]);
  return (
    <Autocomplete
      multiple={!!multiple}
      fullWidth
      name={name}
      open={isOpen}
      onOpen={onOpenHandler}
      onClose={onCloseHandler}
      includeInputInList
      isOptionEqualToValue={isOptionEqualToValue}
      // isOptionEqualToValue={(option, value) =>
      //   option.ClientID === value.ClientID
      // }
      getOptionLabel={getOptionLabel}
      options={options}
      loading={isLoading}
      value={selectedItem}
      onChange={onChangeHandler}
      ListboxProps={{
        onScroll: ({ target, currentTarget }) => {
          if (isRestoringScrollRef.current) return; // SKIP IF RESTORING SCROLL

          listBoxRef.current = target;

          const listboxNode = currentTarget;
          const threshold = 10;
          const isAtBottom = listboxNode.scrollHeight - listboxNode.scrollTop <= listboxNode.clientHeight + threshold;

          if (isAtBottom) {
            loadMoreResults(target, currentTarget);
          }
        },
      }}
      disabled={!!isDisabled}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label={`${label}${required ? ' *' : ''}`}
          variant="outlined"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      filterOptions={filterOptions}
      onInputChange={onInputChangeHandler}
      renderOption={renderOptionHandler}
    />
  );
};

export default memo(AsynAutoComplete);
